// Step-up (ADR-0014): when the API answers 403 STEP_UP_REQUIRED, the client
// asks this dialog to confirm the user's second factor through the existing
// backend mechanism (/auth/step-up/start → /auth/step-up/verify), then the
// original request is retried once. The code is never stored or logged.
import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import { api, call, csrf, registerStepUpHandler } from "@/api/client";
import { ApiProblem } from "@/api/errors";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/ui/dialog";
import { Field } from "@/ui/field";
import { Input } from "@/ui/input";

export function StepUpDialog() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  useEffect(() => {
    registerStepUpHandler(async () => {
      try {
        await call(() => api.POST("/auth/step-up/start"));
      } catch (problem) {
        setError(problem instanceof ApiProblem ? problem.message : undefined);
      }
      setCode("");
      setOpen(true);
      return new Promise<boolean>((resolve) => {
        resolver.current = resolve;
      });
    });
    return () => {
      registerStepUpHandler(null);
    };
  }, []);

  const finish = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpen(false);
    setError(undefined);
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setPending(true);
    try {
      const result = await call(() => api.POST("/auth/step-up/verify", { body: { code } }));
      csrf.set(result.csrfToken);
      finish(true);
    } catch (problem) {
      setError(problem instanceof ApiProblem ? problem.message : "No pudimos verificar el código.");
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) finish(false);
      }}
    >
      <DialogContent
        title="Confirma tu identidad"
        description="Esta acción requiere tu segundo factor. Escribe el código de 6 dígitos de tu app de autenticación."
      >
        <form className="grid gap-4" onSubmit={(e) => void submit(e)}>
          <Field label="Código de verificación" error={error}>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, ""));
              }}
              required
            />
          </Field>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                finish(false);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={pending || code.length !== 6}>
              Verificar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
